import { useCallback, useEffect, useMemo, useState } from 'react'
import { MdDownload } from 'react-icons/md'

import { NotificationType, notify } from 'src/providers/NotificationProvider'

import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { ButtonRebrandVariant } from 'src/components/atoms/ButtonRebrand/styles'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'

import {
  useCreateAttachmentMutation,
  useGeneratePresignedUrlLazyQuery,
  useGetAttachmentLazyQuery,
  useGetPurchaseAttachmentStatusLazyQuery,
  useRegeneratePdfMutation
} from 'src/config/generated/graphql'
import colors from 'src/styles/theme/colors'
import { ATTACHMENT_TYPE_VALUES, AttachmentType } from 'src/types/attachment'

export interface DownloadAttachmentButtonProps {
  /**
   * Providing attachment id will download/regenerate the attachment.  This takes precedence over referenceId
   */
  attachmentId?: string
  attachmentType: AttachmentType
  year?: number | null
  /**
   * Providing reference id (purchase id) will create a new attachment if attachmentId is not provided or null
   */
  referenceId?: string
  variant?: ButtonRebrandVariant
  text?: string
  className?: string
}

const POLL_INTERVAL = 500

export const DownloadAttachmentButton = ({
  attachmentId,
  attachmentType,
  year,
  referenceId,
  variant = 'lime',
  text = '',
  className = ''
}: DownloadAttachmentButtonProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [regeneratePdf, { loading: regeneratingPdf }] =
    useRegeneratePdfMutation({
      onCompleted: data => {
        setIsLoading(false)
        window.location.assign(data?.regenerate_pdf.PresignedUrl)
      }
    })

  const [
    getAttachmentStatus,
    { data: attachmentStatus, stopPolling: stopStatusPolling }
  ] = useGetPurchaseAttachmentStatusLazyQuery({ pollInterval: POLL_INTERVAL })

  const [getAttachment, { data: attachmentData, stopPolling }] =
    useGetAttachmentLazyQuery({ pollInterval: POLL_INTERVAL })

  const [createAttachment, { loading: creating }] = useCreateAttachmentMutation(
    {
      onCompleted: data => {
        getAttachment({ variables: { id: data?.insert_Attachment_one?.Id } })
      }
    }
  )

  useEffect(() => {
    if (attachmentType === AttachmentType.CertificatesPackaged && referenceId) {
      getAttachmentStatus({
        variables: {
          purchaseId: referenceId,
          types: [AttachmentType.CertificateBulk]
        }
      })
    }
  }, [attachmentType, getAttachmentStatus, referenceId])

  const allAttachmentsReady = useMemo(() => {
    if (attachmentType !== AttachmentType.CertificatesPackaged) {
      return true
    }
    return attachmentStatus?.attachmentStatus?.RelatedPurchases.every(
      p =>
        p.Attachment.length === 0 ||
        p.Attachment.every(a => a.Generated && !a.Failed)
    )
  }, [attachmentStatus?.attachmentStatus?.RelatedPurchases, attachmentType])

  useEffect(() => {
    if (
      attachmentType === AttachmentType.CertificatesPackaged &&
      allAttachmentsReady
    ) {
      stopStatusPolling()
    }
  }, [allAttachmentsReady, attachmentType, stopStatusPolling])

  const [generatePresignedUrl] = useGeneratePresignedUrlLazyQuery({
    onCompleted: data => {
      setIsLoading(false)
      window.location.assign(data?.presignedUrl)
    }
  })

  useEffect(() => {
    const { attachment } = attachmentData || {}
    const { Generated, Filename, Failed } = attachment || {}

    if (Generated && Filename) {
      stopPolling()

      generatePresignedUrl({ variables: { path: Filename } })
    }

    if (Failed) {
      stopPolling()
      setIsLoading(false)
      notify({
        message: 'Could not generate attachment',
        type: NotificationType.error
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentData])

  const handleDownloadAttachment = useCallback(() => {
    setIsLoading(true)
    if (attachmentId) {
      regeneratePdf({ variables: { input: { AttachmentId: attachmentId } } })
    } else if (referenceId) {
      createAttachment({
        variables: {
          input: {
            ReferenceId: referenceId,
            Year: year,
            Type: attachmentType
          }
        }
      })
    }
  }, [
    attachmentId,
    attachmentType,
    createAttachment,
    referenceId,
    regeneratePdf,
    year
  ])

  const attachmentLabel = useMemo(() => {
    if (attachmentType === AttachmentType.TaxReceipt) {
      return `Tax certificate (year ending ${year})`
    }
    if (attachmentType === AttachmentType.CertificatesPackaged) {
      return 'zip'
    }
    return ATTACHMENT_TYPE_VALUES[attachmentType]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentId, attachmentType, year])

  const isDisabled =
    regeneratingPdf || isLoading || creating || !allAttachmentsReady
  const isSpinning = regeneratingPdf || isLoading || creating

  const content = (
    <div className='flex space-x-2 items-center'>
      <LoadingSpinner
        color={colors.forestDeep}
        isLoading={isSpinning}
        size={20}
      >
        <MdDownload />
      </LoadingSpinner>
      <span>
        {allAttachmentsReady
          ? text || `Download ${attachmentLabel}`
          : 'Generating gifts...'}
      </span>
    </div>
  )

  return (
    <ButtonRebrand
      className={className}
      variant={variant}
      disabled={isDisabled}
      onClick={handleDownloadAttachment}
    >
      {content}
    </ButtonRebrand>
  )
}
